import { useEffect, useReducer, useRef } from 'react';

const SET_CHANGED = 'SET_CHANGED';
const SET_ERRORED = 'SET_ERRORED';
const SET_FOCUSED = 'SET_FOCUSED';
const SET_HOVERED = 'SET_HOVERED';

type TFormControlState = {
  initialValue: string;
  isDirty: boolean;
  isEmpty: boolean;
  isErrored: boolean;
  isFocused: boolean;
  isHovered: boolean;
  isTouched: boolean;
  characterCount: number;
};

type TFormControlHandler = {
  handleFocus: (e: any) => void;
  handleBlur: (e: any) => void;
  handleMouseOver: (e: any) => void;
  handleMouseOut: (e: any) => void;
  handleChange: (e: any) => void;
};

const init = ({ initialValue, errorText }): TFormControlState => ({
  initialValue,
  isDirty: false,
  isEmpty: !initialValue || !initialValue.length,
  isErrored: errorText?.length > 0,
  isFocused: false,
  isHovered: false,
  isTouched: false,
  characterCount: initialValue?.length ?? 0,
});

const reducer = (state, { type, value }) => {
  switch (type) {
    case SET_CHANGED:
      return {
        ...state,
        isDirty: value !== state.initialValue,
        isEmpty: !value || value.length <= 0,
        isErrored: state.isErrored || state?.errorText?.length > 0,
        characterCount: value?.length ?? 0,
      };
    case SET_ERRORED:
      return {
        ...state,
        isErrored: value?.length > 0,
      };
    case SET_FOCUSED:
      return {
        ...state,
        isFocused: value,
        isTouched: true,
      };
    case SET_HOVERED:
      return {
        ...state,
        isHovered: value,
      };
    default:
      throw new Error();
  }
};

type TUseFormControlReducerArgs = {
  onChange?: (e: any) => void;
  onBlur?: (e: any) => void;
  onFocus?: (e: any) => void;
  onMouseOver?: (e: any) => void;
  onMouseOut?: (e: any) => void;
  value?: string;
  errorText?: string;
};

const useFormControlReducer = ({
  onChange,
  onFocus,
  onBlur,
  onMouseOver,
  onMouseOut,
  value = '',
  errorText,
}: TUseFormControlReducerArgs): { state: TFormControlState; handlers: TFormControlHandler } => {
  // set initialValue on first render to calculate `isDirty`
  const initialValue = useRef(value).current;
  const [state, dispatch] = useReducer(reducer, { initialValue, errorText }, init);

  useEffect(() => {
    dispatch({ type: SET_CHANGED, value: value });
  }, [value]);

  useEffect(() => {
    dispatch({ type: SET_ERRORED, value: errorText });
  }, [errorText]);

  const handleFocus = (e) => {
    dispatch({ type: SET_FOCUSED, value: true });
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  };
  const handleBlur = (e) => {
    dispatch({ type: SET_FOCUSED, value: false });
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
  };
  const handleMouseOver = (e) => {
    dispatch({ type: SET_HOVERED, value: true });
    if (typeof onMouseOver === 'function') {
      onMouseOver(e);
    }
  };

  const handleMouseOut = (e) => {
    dispatch({ type: SET_HOVERED, value: false });
    if (typeof onMouseOut === 'function') {
      onMouseOut(e);
    }
  };

  const handleChange = (e) => {
    dispatch({ type: SET_CHANGED, value: e.target.value });
    if (typeof onChange === 'function') {
      onChange(e);
    }
  };

  return {
    state,
    handlers: {
      handleFocus,
      handleBlur,
      handleMouseOver,
      handleMouseOut,
      handleChange,
    },
  };
};

export default useFormControlReducer;
